/**
 * storefrontApi.js
 * Helper for all /api/tenant/:uuid/storefront/* calls.
 * Auth: Bearer {{publicApiKey}} — separate from the backoffice JWT.
 *
 * tenant_uuid → selectedAccount.id
 * apiKey      → selectedAccount.publicApiKey  (from GET /api/accounts)
 */

import axios from 'axios';
import secureStorage from '../utils/secureStorage';
import { API_URL } from '../config/api';

// Cache keyed by account id so switching accounts busts it automatically
let _cache = null; // { accountId, uuid, apiKey, baseURL }

const getCredentials = async () => {
    const account = await secureStorage.getItem('selectedAccount');

    if (!account?.id) throw new Error('Aucun compte sélectionné.');

    // Debug — remove once auth is confirmed working
    console.log('[storefrontApi] selectedAccount keys:', Object.keys(account));
    console.log('[storefrontApi] publicApiKey value:', account.publicApiKey);
    console.log('[storefrontApi] account.id:', account.id);

    // The storefront Bearer token is the publicApiKey issued by the platform.
    // Field name comes from GET /api/accounts response.
    const apiKey =
        account.publicApiKey    ||  // camelCase (JS)
        account.public_api_key  ||  // snake_case (some backends)
        account.apiKey          ||  // legacy alias
        '';

    if (!apiKey) {
        console.warn('[storefrontApi] ⚠️  No publicApiKey found in selectedAccount. All storefront requests will return 401.');
    }

    return {
        accountId: account.id,
        uuid:      account.id,
        apiKey,
        baseURL:   API_URL,
    };
};

/**
 * Build an axios instance for the current account's storefront.
 * Cache is invalidated when the selected account changes.
 */
export const getStorefrontApi = async (refresh = false) => {
    const account = await secureStorage.getItem('selectedAccount');
    const currentId = account?.id;

    // Bust cache if account changed or forced
    if (!_cache || refresh || _cache.accountId !== currentId) {
        _cache = await getCredentials();
    }

    const { uuid, apiKey, baseURL } = _cache;

    const instance = axios.create({
        baseURL: `${baseURL}/tenant/${uuid}/storefront`,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(apiKey ? { Authorization: `Bearer ${apiKey}` } : {}),
        },
    });

    return { api: instance, uuid };
};

/** Convenience wrappers --------------------------------------------------- */

export const storefrontGet    = async (path, params) => {
    const { api } = await getStorefrontApi();
    return api.get(path, { params });
};

export const storefrontPost   = async (path, data) => {
    const { api } = await getStorefrontApi();
    return api.post(path, data);
};

export const storefrontPut    = async (path, data) => {
    const { api } = await getStorefrontApi();
    return api.put(path, data);
};

export const storefrontDelete = async (path) => {
    const { api } = await getStorefrontApi();
    return api.delete(path);
};
